import { useEffect, useRef, useState } from 'react'
import { phoneCellWidthClass } from '@/components/evidence/cell-width'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { SourceSnippet } from '@/types/source-snippet'
import { cn } from '@/utils/cn'

function cellText(value: unknown) {
  if (value === null || value === undefined) return '—'
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

export function SnippetRows({
  snippet,
  focusedRow,
}: {
  snippet: SourceSnippet
  focusedRow?: number
}) {
  const [page, setPage] = useState(Math.floor((focusedRow ?? 0) / 50))
  const selected = useRef<HTMLTableRowElement>(null)
  const { rows, columns } = snippet.snapshot.result
  useEffect(() => {
    setPage(Math.floor((focusedRow ?? 0) / 50))
  }, [focusedRow])
  useEffect(() => {
    if (focusedRow !== undefined && Math.floor(focusedRow / 50) === page)
      selected.current?.scrollIntoView?.({ block: 'nearest' })
  }, [page, focusedRow])
  const pageCount = Math.max(1, Math.ceil(rows.length / 50))
  return (
    <div className="space-y-2">
      <div className="max-h-96 overflow-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {/* Phone: the two position headers wrap, so they do not push the data off screen. */}
              <TableHead className="max-md:whitespace-normal">Result row</TableHead>
              {columns.map(column => (
                <TableHead
                  key={column}
                  className={cn(
                    'whitespace-nowrap',
                    column === '__source_row' && 'max-md:min-w-24 max-md:whitespace-normal'
                  )}
                >
                  {column === '__source_row' ? 'Source position (0-based)' : column}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            <ResultRows context={{ rows, columns, page, focusedRow, selected }} />
          </TableBody>
        </Table>
      </div>
      <RowPagination page={page} pageCount={pageCount} onChange={setPage} />
    </div>
  )
}

function RowPagination({
  page,
  pageCount,
  onChange,
}: {
  page: number
  pageCount: number
  onChange: (page: number) => void
}) {
  return (
    <>
      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            className="max-md:h-9"
            disabled={page === 0}
            onClick={() => onChange(page - 1)}
          >
            Previous
          </Button>
          <span>
            {page + 1} / {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="max-md:h-9"
            disabled={page + 1 >= pageCount}
            onClick={() => onChange(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </>
  )
}

function ResultRows({
  context,
}: {
  context: {
    rows: Record<string, unknown>[]
    columns: string[]
    page: number
    focusedRow?: number
    selected: React.RefObject<HTMLTableRowElement | null>
  }
}) {
  const { rows, columns, page, focusedRow, selected } = context
  return (
    <>
      {rows.slice(page * 50, (page + 1) * 50).map((row, offset) => {
        const index = page * 50 + offset
        return (
          <TableRow
            key={index}
            ref={index === focusedRow ? selected : undefined}
            className={cn(index === focusedRow && 'bg-accent')}
            aria-selected={index === focusedRow}
          >
            <TableCell className="text-muted-foreground">{index + 1}</TableCell>
            {columns.map(column => {
              const text = cellText(row[column])
              return (
                <TableCell
                  key={column}
                  className={cn(
                    'max-w-sm whitespace-pre-wrap break-words font-mono text-xs',
                    phoneCellWidthClass(text)
                  )}
                >
                  {text}
                </TableCell>
              )
            })}
          </TableRow>
        )
      })}
    </>
  )
}
